import { useEffect, useState } from "react";
import { PageHero, Container } from "../components/Section";
import { api, API } from "../lib/api";
import { Play, FileText, Image as ImageIcon, Volume2, Film } from "lucide-react";
import { motion } from "framer-motion";

function iconFor(ct = "") {
  if (ct.startsWith("video/")) return Film;
  if (ct.startsWith("audio/")) return Volume2;
  if (ct.startsWith("image/")) return ImageIcon;
  return FileText;
}

export default function MediaLibrary() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="media-library-page">
      <PageHero
        overline="Media Library"
        title={<>Words, music, & <em className="text-gold-light italic font-light">moments</em> to revisit.</>}
        kicker="Our growing archive of sermons, songs, photos, and special services."
        image="/images/branding/pastor-worship.jpg"
      />

      <Container className="pb-32">
        {items.length === 0 ? (
          <div className="text-center py-20 glass">
            <p className="font-serif italic text-white/60 text-lg">The media library is being curated. Check back soon.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((m, i) => {
              const Icon = iconFor(m.content_type);
              const isImage = m.content_type?.startsWith("image/");
              const isAudio = m.content_type?.startsWith("audio/");
              const isVideo = m.content_type?.startsWith("video/");
              const fileUrl = `${API}/media/file/${m.id}`;
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                  className="glass overflow-hidden group"
                  data-testid={`media-item-${m.id}`}
                >
                  <div className="aspect-video bg-wine-900 relative overflow-hidden">
                    {isImage && <img src={fileUrl} alt={m.title} className="w-full h-full object-cover" />}
                    {isVideo && (
                      <video controls className="w-full h-full object-cover" preload="metadata">
                        <source src={fileUrl} type={m.content_type} />
                      </video>
                    )}
                    {!isImage && !isVideo && (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-wine-800 to-wine-950">
                        <Icon className="h-12 w-12 text-gold/70 stroke-[1.5]" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-widest text-gold mb-2">{m.category}</div>
                    <h3 className="font-serif text-lg font-bold mb-1">{m.title}</h3>
                    {m.speaker && <div className="text-xs text-white/55 mb-2">{m.speaker}</div>}
                    {m.description && <p className="text-sm text-white/65 line-clamp-3 mt-2">{m.description}</p>}
                    {isAudio && (
                      <audio controls className="w-full mt-3">
                        <source src={fileUrl} type={m.content_type} />
                      </audio>
                    )}
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold hover:text-gold-light">
                      <Play className="h-3 w-3" /> Open
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </Container>
    </div>
  );
}
